import React from 'react';
import { Puzzle } from 'lucide-react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

const DailyPuzzleEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const content = block.content || {};

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <Puzzle size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Daily Chess Puzzle</h4>
            <p className="text-sm text-gray-400">
              Shows the Lichess puzzle of the day. Visitors can solve it
              directly on your website with drag-and-drop or click-to-move.
              Refreshes automatically each day.
            </p>
          </div>
        </div>
      </div>

      {/* Title Input */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">
          Section Title
        </label>
        <input
          type="text"
          value={content.title || 'Daily Puzzle'}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Daily Puzzle"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#749555] transition-colors"
        />
      </div>

      {/* Theme Match Toggle */}
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          handleChange('matchThemeSquares', !content.matchThemeSquares);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleChange('matchThemeSquares', !content.matchThemeSquares);
          }
        }}
        className="flex items-center justify-between p-3 bg-[#1a1a2e] border border-gray-700 rounded-lg cursor-pointer hover:border-gray-600 transition-colors"
      >
        <div>
          <span className="text-sm font-medium text-gray-300">
            Match Theme Color
          </span>
          <p className="text-xs text-gray-500 mt-0.5">
            Use your website's accent color for the dark squares
          </p>
        </div>
        <div
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
            content.matchThemeSquares === true ? 'bg-[#749555]' : 'bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
              content.matchThemeSquares === true ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </div>
      </div>

      {/* Preview Info */}
      <div className="text-sm text-gray-400 space-y-2">
        <p>This block displays:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-500">
          <li>Today's Lichess puzzle with interactive board</li>
          <li>Puzzle rating and difficulty themes</li>
          <li>Move validation with visual feedback</li>
          <li>Reset and solution buttons</li>
        </ul>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={BLOCK_FEATURES['daily-puzzle']}
          />
          <ColorsFontsPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={BLOCK_FEATURES['daily-puzzle']}
            theme={theme || {}}
            blockType="daily-puzzle"
          />
        </>
      )}
    </div>
  );
};

export default DailyPuzzleEditor;
